import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api from "@/lib/api";
import { CASE_TYPES } from "@/lib/constants";
import { ArrowUpRight, CheckCircle2 } from "lucide-react";

const initial = {
  name: "",
  phone: "",
  email: "",
  dispute_type: "",
  message: "",
  preferred_slot: "",
  consent: false,
};

export default function ConsultExpertDialog({ open, onOpenChange }) {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const close = () => {
    onOpenChange(false);
    setTimeout(() => {
      setForm(initial);
      setDone(false);
    }, 250);
  };

  const canSubmit =
    form.name.trim().length > 1 &&
    form.phone.trim().length >= 7 &&
    /.+@.+\..+/.test(form.email) &&
    form.dispute_type &&
    form.consent;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit || loading) return;
    setLoading(true);
    try {
      await api.post("/consultations", form);
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const field =
    "h-11 rounded-none bg-transparent border-0 border-b border-ink/20 focus-visible:ring-0 focus:border-clay text-ink placeholder:text-ink/40 px-0";

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? onOpenChange(true) : close())}>
      <DialogContent
        data-testid="consult-expert-dialog"
        className="bg-sand text-ink border-0 sm:max-w-[560px] p-0 [&>button]:text-ink/60"
        style={{ borderRadius: 4 }}
      >
        {done ? (
          <div className="p-8 sm:p-10 text-center" data-testid="consult-expert-success">
            <div className="mx-auto w-12 h-12 grid place-items-center bg-leaf/15 text-leaf" style={{ borderRadius: 999 }}>
              <CheckCircle2 className="h-6 w-6" />
            </div>
            <DialogHeader className="mt-5">
              <DialogTitle className="font-display text-[28px] text-ink text-center" style={{ fontWeight: 400 }}>
                Request received.
              </DialogTitle>
            </DialogHeader>
            <p className="mt-3 text-ink/70 text-[14px]">
              A member of our team will reach out within one business day to confirm your slot and share offline payment details for the ₹2,500 consultation fee.
            </p>
            <button
              onClick={close}
              data-testid="consult-expert-close"
              className="mt-8 inline-flex items-center gap-2 px-5 py-3 text-sm font-medium bg-clay text-sand hover:bg-[hsl(17_64%_42%)]"
              style={{ borderRadius: 3 }}
            >
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-8 sm:p-10">
            <div className="font-mono text-[11px] tracking-[0.16em] uppercase text-clay">Consult an expert</div>
            <DialogHeader className="mt-3">
              <DialogTitle className="font-display text-[26px] sm:text-[30px] text-ink text-left" style={{ fontWeight: 400, lineHeight: 1.1 }}>
                60-minute consultation with a qualified advocate.
              </DialogTitle>
            </DialogHeader>
            <p className="mt-2 text-ink/65 text-[13px]">
              Fee ₹2,500 — collected offline once your slot is confirmed by our team.
            </p>

            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
              <div className="sm:col-span-2">
                <Input
                  data-testid="consult-name"
                  placeholder="Full name *"
                  value={form.name}
                  onChange={(e) => set("name", e.target.value)}
                  className={field}
                />
              </div>
              <Input
                data-testid="consult-phone"
                placeholder="Phone *"
                inputMode="tel"
                value={form.phone}
                onChange={(e) => set("phone", e.target.value)}
                className={field}
              />
              <Input
                data-testid="consult-email"
                type="email"
                placeholder="Email *"
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
                className={field}
              />
              <div className="sm:col-span-2">
                <Select value={form.dispute_type} onValueChange={(v) => set("dispute_type", v)}>
                  <SelectTrigger data-testid="consult-type-trigger" className={field}>
                    <SelectValue placeholder="Type of dispute *" />
                  </SelectTrigger>
                  <SelectContent>
                    {CASE_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <Textarea
                  data-testid="consult-message"
                  placeholder="Brief description (optional)"
                  value={form.message}
                  onChange={(e) => set("message", e.target.value)}
                  className="rounded-none bg-transparent border-0 border-b border-ink/20 focus-visible:ring-0 focus:border-clay text-ink placeholder:text-ink/40 px-0 min-h-[80px]"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block font-mono text-[11px] tracking-[0.12em] uppercase text-ink/55 mb-2">
                  Preferred slot (optional)
                </label>
                <Input
                  data-testid="consult-slot"
                  type="datetime-local"
                  value={form.preferred_slot}
                  onChange={(e) => set("preferred_slot", e.target.value)}
                  className={field}
                />
              </div>
            </div>

            <label className="mt-6 flex items-start gap-3 text-[12.5px] text-ink/75 select-none">
              <input
                type="checkbox"
                data-testid="consult-consent"
                checked={form.consent}
                onChange={(e) => set("consent", e.target.checked)}
                className="mt-1 h-4 w-4 accent-[hsl(17_64%_47%)]"
              />
              <span>
                I understand that eSulah is <span className="text-ink font-medium">not a law firm</span>. I consent to being contacted by the eSulah team to confirm my slot and arrange the ₹2,500 consultation fee offline.
              </span>
            </label>

            <div className="mt-7 flex items-center justify-between gap-4">
              <button type="button" onClick={close} className="text-[13px] text-ink/60 hover:text-ink" data-testid="consult-cancel">
                Cancel
              </button>
              <button
                type="submit"
                disabled={!canSubmit || loading}
                data-testid="consult-submit"
                className="group inline-flex items-center gap-2 px-5 py-3 text-sm font-medium bg-clay text-sand hover:bg-[hsl(17_64%_42%)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                style={{ borderRadius: 3 }}
              >
                {loading ? "Submitting…" : "Request consultation"}
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
